'use client';

import type { Voice } from 'ml-text-service-client';
import React, { createContext, useContext, PropsWithChildren } from 'react';
import { JSX } from 'react';
import type { User, Subscription as SbSubscription } from 'stockblocks-client';
import { OptIns, type User as RecurlyUser } from 'user-service-client';

import type { Subscription } from '@videoblocks/subscription-service-client';

import type { AiUserSegment } from '../../ai/_shared/_context/types';
import { getAccountCode } from '../utils';

export type CreditsApi = {
    refreshCredits: () => Promise<number>;
    calculateCost: (subject: string | number | File) => Promise<number>;
};

export type VoiceoverContextValue = {
    contributorVoices: Voice[];
    voices: Voice[];
    user: User | null;
    credits: number;
    CreditsApi: CreditsApi;
    optIns: OptIns | null;
    subscription: SbSubscription | null;
    recurlySubscription: Subscription | null;
    recurlyUser: RecurlyUser | null;
    voiceoverSegment: AiUserSegment;
    isEnterprise: boolean;
    accountCode: string | null;
};

export type VoiceoverProviderType = Omit<VoiceoverContextValue, 'accountCode'>;

const VoiceoverContext = createContext<VoiceoverContextValue | undefined>(undefined);

export function VoiceoverProvider({
    children,
    ...contextProps
}: PropsWithChildren<VoiceoverProviderType>): JSX.Element {
    const { credits, CreditsApi, ...otherContextProps } = contextProps;
    const [_credits, setCredits] = React.useState(credits);

    const { user, subscription } = otherContextProps;

    const accountCode = React.useMemo(
        () => getAccountCode(user?.id, subscription?.organizationId),
        [user?.id, subscription?.organizationId],
    );

    const refreshCredits = async () => {
        const updatedCredits = await CreditsApi.refreshCredits();
        setCredits(updatedCredits);
        return updatedCredits;
    };

    return (
        <VoiceoverContext.Provider
            value={{
                ...otherContextProps,
                accountCode,
                credits: _credits,
                CreditsApi: {
                    refreshCredits,
                    calculateCost: CreditsApi.calculateCost,
                },
            }}
        >
            {children}
        </VoiceoverContext.Provider>
    );
}

export function useVoiceoverContext(): VoiceoverContextValue {
    const ctx = useContext(VoiceoverContext);
    if (!ctx) throw new Error('useVoiceoverContext must be used within VoiceoverProvider');
    return ctx;
}
